import {
  useContext,
  useState,
  useRef,
  useEffect,
  startTransition,
} from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import {
  Bell,
  Volume2,
  VolumeX,
  LogOut,
  ShoppingCart,
  Headphones,
  Megaphone,
  UserCircle,
  ChevronDown,
  X,
  Search,
} from "lucide-react";
import Logo from "../../images/MY-Anlook.png";
import { useNavigate } from "react-router-dom";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import { ToastContainer } from "react-toastify";

export default function Header() {
  const { isMuted, toggleMute, unreadOrders, clearUnreadOrders, wsConnected } =
    useContext(GlobalContext);

  const navigate = useNavigate();
  const [showProfileCard, setShowProfileCard] = useState(false);
  const profileCardRef = useRef(null);

  const userData = JSON.parse(sessionStorage.getItem("user"));
  const userName =
    userData?.name || userData?.account_name || userData?.username || "Account";
  const [orgDetails, setOrgDetails] = useState(null);
  const { fetchOrganizationDetails } = useProfileDashboard();
  const user = JSON.parse(sessionStorage.getItem("user")) || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const searchRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  const getSearchData = () => {
    const data = JSON.parse(sessionStorage.getItem("entitlement")) || [];

    let items = [];

    data.forEach((module) => {
      if (module.module_name && module.module_url) {
        items.push({
          name: module.module_name.trim(),
          url: module.module_url.trim(),
        });
      }

      (module.services || []).forEach((service) => {
        if (
          service.service_name &&
          service.service_url &&
          service.service_name !== "null" &&
          service.service_url !== "null"
        ) {
          items.push({
            name: `${module.module_name} - ${service.service_name}`.trim(),
            url: service.service_url.trim(),
          });
        }
      });
    });

    const uniqueItems = items.filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (i) =>
            i.name.toLowerCase() === item.name.toLowerCase() &&
            i.url.toLowerCase() === item.url.toLowerCase(),
        ),
    );

    return uniqueItems;
  };

 const handleSelect = (url) => {
  setSearchTerm("");
  setResults([]);

  startTransition(() => {
    navigate(url);
  });
};

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    const allData = getSearchData();

    const filtered = allData.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    setResults(filtered);
  }, [searchTerm]);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await fetchOrganizationDetails();
        if (res?.responseCode === "00") {
          setOrgDetails(res.response);
        }
      } catch (err) {
        console.error("Org details error", err);
      }
    };
    fetchOrg();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        profileCardRef.current &&
        !profileCardRef.current.contains(e.target)
      ) {
        setShowProfileCard(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigate = (url) => {
  startTransition(() => {
    navigate(url);
  });
};

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    navigate("/");
  };

  useEffect(() => {
    if (searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !searchRef.current.contains(e.target)
      ) {
        setResults([]);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
    <ToastContainer/>
   
    <div className="sticky top-0 z-50 w-full border-b shadow-sm bg-green-100 montserrat-font-1">
      <div className="flex items-center justify-between h-[60px] px-2 sm:px-3 md:px-4 lg:px-6 gap-1 sm:gap-2 overflow-hidden">
        {/* LEFT — Search */}
        <div className="flex items-center flex-shrink-0">
          <div className="relative w-[80px] sm:w-[120px] md:w-[140px] lg:w-[260px] xl:w-[320px]">
            <div className="flex items-center bg-white border border-gray-300 rounded-full px-2 sm:px-3 py-1 shadow-sm">
              <Search
                size={14}
                className="text-gray-400 mr-1 sm:mr-2 flex-shrink-0"
              />
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full outline-none text-xs sm:text-sm bg-transparent"
              />
            </div>

            {results.length > 0 && (
              <div
                ref={dropdownRef}
                className="fixed bg-white shadow-lg rounded-xl border z-[9999] max-h-60 overflow-y-auto"
                style={{
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                }}
              >
                {results.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelect(item.url)}
                   className="px-4 py-2 text-sm hover:bg-green-100 cursor-pointer transition-colors"
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CENTER — Logo */}
        <div className="flex-shrink-0 flex justify-center px-1 sm:px-2">
          <img
            src={Logo}
            alt="Logo"
            className="h-5 sm:h-6 md:h-7 lg:h-8 object-contain"
          />
        </div>

        {/* RIGHT — Action buttons */}
        <div className="flex items-center justify-end gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">
          {/* Orders — md+ pe show */}
          <button
            onClick={() => handleNavigate("/orders")}
           className="hidden sm:flex p-1.5 md:p-2 rounded-md border border-green-300 transition-colors bg-white hover:bg-green-50"
            title="Orders"
          >
            <ShoppingCart
              size={16}
              className="text-green-600 md:w-[18px] md:h-[18px]"
            />
          </button>

          {/* Support — md+ pe show */}
          <button
           onClick={() => handleNavigate("/Support/Tickets")}
          className="hidden md:flex p-1.5 md:p-2 rounded-md border border-green-300 transition-colors bg-white hover:bg-green-50"
            title="Support"
          >
            <Headphones
              size={16}
              className="text-green-600 md:w-[18px] md:h-[18px]"
            />
          </button>

          {/* Campaign — lg+ pe show */}
          <button
           onClick={() => handleNavigate("/WhatsApp/Manage")}
           className="hidden lg:flex p-1.5 md:p-2 rounded-md border border-green-300 transition-colors bg-white hover:bg-green-50"
            title="Campaign"
          >
            <Megaphone
              size={16}
              className="text-green-600 md:w-[18px] md:h-[18px]"
            />
          </button>

          {/* Mute — hamesha show */}
          <button
            onClick={toggleMute}
            className="p-1.5 md:p-2 rounded-md border border-green-300 transition-colors bg-white hover:bg-green-50"
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <VolumeX
                size={16}
                className="text-red-600 md:w-[18px] md:h-[18px]"
              />
            ) : (
              <Volume2
                size={16}
                className="text-green-600 md:w-[18px] md:h-[18px]"
              />
            )}
          </button>

          {/* Notifications — hamesha show */}
          <button
            title="Notifications"
            onClick={() => {
              sessionStorage.setItem(
                "highlightOrders",
                JSON.stringify(unreadOrders),
              );
              clearUnreadOrders();
              handleNavigate("/orders");
            }}
           className="relative p-1.5 md:p-2 rounded-md border border-green-300 transition-colors bg-white hover:bg-green-50"
          >
            <Bell
              size={16}
              className="text-green-600 md:w-[18px] md:h-[18px]"
            />
            {unreadOrders.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] h-[16px] px-0.5 flex items-center justify-center font-bold">
                {unreadOrders.length > 10 ? "10+" : unreadOrders.length}
              </span>
            )}
          </button>

          {/* Profile */}
          <div className="relative" ref={profileCardRef}>
            <div
              onClick={() => setShowProfileCard(!showProfileCard)}
            className="flex items-center gap-1 sm:gap-2 bg-white px-2 sm:px-3 py-1.5 rounded-lg cursor-pointer transition-colors hover:bg-green-50"
            >
              <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 p-0.5 rounded-full overflow-hidden border border-green-300 flex items-center justify-center bg-white flex-shrink-0">
                {orgDetails?.brand_image ? (
                  <img
                    src={orgDetails.brand_image}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircle size={14} className="text-green-600" />
                )}
              </div>

              {/* Name — sirf md+ pe dikhega */}
              <span className="hidden md:block text-xs lg:text-sm font-medium text-gray-700 max-w-[80px] lg:max-w-[120px] truncate">
                {userName}
              </span>

              <ChevronDown
                size={12}
                className={`text-gray-500 transition-transform flex-shrink-0 ${showProfileCard ? "rotate-180" : ""}`}
              />
            </div>

            {/* Profile dropdown — same rahega */}
            {showProfileCard && (
              <div className="fixed top-16 right-2 sm:right-4 md:right-6 w-64 sm:w-72 bg-white rounded-2xl shadow-xl z-[9999] border border-gray-100">
                <div className="flex justify-between items-center px-5 py-4 border-b">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {user.username}
                    </span>
                    {
                      <span className=" text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {user.userRole}
                      </span>
                    }
                  </div>

                  <X
                    size={16}
                    className="cursor-pointer text-gray-400 hover:text-gray-600 flex-shrink-0"
                    onClick={() => setShowProfileCard(false)}
                  />
                </div>
                <div className="flex flex-col items-center text-center px-4 py-4">
                  <div className="w-16 h-16 rounded-full border-4 border-green-200 flex items-center justify-center mb-2 overflow-hidden p-1">
                    {orgDetails?.brand_image ? (
                      <img
                        src={orgDetails.brand_image}
                        alt="Brand"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <UserCircle size={36} className="text-gray-400" />
                    )}
                  </div>
                  <h3 className="text-base font-bold text-gray-800">
                    {orgDetails?.organization_name || userName.split(" ")[0]}
                  </h3>
                  <div className="mt-1 space-y-1">
                    {orgDetails?.gst && (
                      <p className="text-xs text-gray-700">
                        GST: {orgDetails.gst}
                      </p>
                    )}

                    {user?.mobile && (
                      <p className="text-xs text-gray-700">
                        Mobile: {user.mobile}
                      </p>
                    )}

                    {user?.email && (
                      <p className="text-xs text-gray-700 break-all">
                        Email: {user.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <button
                    onClick={handleLogout}
                   className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2 text-gray-700 hover:bg-red-100 transition-colors text-sm"
                  >
                    <LogOut size={15} /> Log out
                  </button>
                </div>
                <div className="text-xs text-gray-700 flex justify-center gap-2 pb-3">
                  <span
                    className="cursor-pointer hover:underline"
                   onClick={() => handleNavigate("/privacy-policy")}
                  >
                    Privacy Policy
                  </span>
                  <span>·</span>
                  <span
                    className="cursor-pointer hover:underline"
                   onClick={() => handleNavigate("/terms-conditions")}
                  >
                    Terms of Service
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* WS Status dot */}
          <div
            title={wsConnected ? "Connected" : "Disconnected"}
            className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full border border-gray-300 bg-white shadow-sm flex-shrink-0"
          >
            <span
              className={`w-2.5 h-2.5 rounded-full ${wsConnected ? "bg-green-500 animate-pulse" : "bg-red-500 animate-pulse"}`}
            ></span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
